import { useMatchRoute } from '@tanstack/react-router';
import classNames from 'classnames';
import type { LucideProps } from 'lucide-react';
import type { ComponentType } from 'react';
import { Icon } from '@/components/Icon';
import { Skeleton } from '@/components/Skeleton';
import type { FileRouteTypes } from '@/routeTree.gen';
import { RouterLink } from '../Link';
import * as styles from './index.css';

export type RouteTab = {
    label: string;
    icon: ComponentType<LucideProps>;
    to: FileRouteTypes['to'];
    fuzzy?: boolean;
    search?: Record<string, unknown>;
};

type RouteTabsProps = {
    tabs: readonly RouteTab[];
};

export function RouteTabs({ tabs }: RouteTabsProps) {
    const matchRoute = useMatchRoute();

    return (
        <div role="tablist" className={styles.tabList}>
            {tabs.map((tab) => {
                const isActive = Boolean(matchRoute({ to: tab.to, fuzzy: tab.fuzzy }));

                return (
                    <RouterLink
                        key={tab.to}
                        role="tab"
                        aria-selected={isActive}
                        className={classNames(styles.tab, { [styles.tabActive]: isActive })}
                        to={tab.to}
                        search={tab.search}
                        // Switching tabs shouldn't grow browser history — otherwise "back" from a
                        // tabbed page only undoes the last tab switch instead of leaving the page.
                        replace
                    >
                        <Icon icon={tab.icon} size="sm" />
                        {tab.label}
                    </RouterLink>
                );
            })}
        </div>
    );
}

export type Tab<TId extends string = string> = {
    id: TId;
    label: string;
    icon: ComponentType<LucideProps>;
};

type TabsProps<TId extends string> = {
    tabs: readonly Tab<TId>[];
    activeTabId: TId;
    onTabChange: (tabId: TId) => void;
};

/**
 * @deprecated Use the router-integrated `RouteTabs` instead.
 */
export function Tabs<TId extends string>({ tabs, activeTabId, onTabChange }: TabsProps<TId>) {
    return (
        <div role="tablist" className={styles.tabList}>
            {tabs.map((tab) => (
                <button
                    key={tab.id}
                    role="tab"
                    type="button"
                    aria-selected={tab.id === activeTabId}
                    className={classNames(styles.tab, { [styles.tabActive]: tab.id === activeTabId })}
                    onClick={() => onTabChange(tab.id)}
                >
                    <Icon icon={tab.icon} size="sm" />
                    {tab.label}
                </button>
            ))}
        </div>
    );
}

type TabsSkeletonProps = {
    /** Width of the placeholder bar. @default 320 */
    width?: number | string;
};

/** Placeholder tab bar for `Tabs`/`RouteTabs` when the tab set isn't known yet (e.g. labels depend on unloaded data). */
export function TabsSkeleton({ width = 320 }: TabsSkeletonProps) {
    return <Skeleton.Box width={width} height={56} />;
}
